import React, { useCallback, useMemo, useRef, useState } from 'react';
import { RiArrowDownSLine, RiArrowUpSLine, RiSearchLine } from '@remixicon/react';

export interface AppSelectItemProp {
  value: string;
  label: string;
}

interface AppSelectProps {
  value: AppSelectItemProp[];
  onChange: (value: AppSelectItemProp[]) => void;
  options: AppSelectItemProp[];
  isSearchable?: boolean;
}

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 7;
const CONTAINER_HEIGHT = ITEM_HEIGHT * VISIBLE_COUNT;
const OVERSCAN = 5;

export const AppMultiselect = ({ isSearchable = false, options, onChange, value }: AppSelectProps) => {
  const [valueCheck, setValueCheck] = useState(value);
  const [search, setSearch] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [mouseEnter, setMouseEnter] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Sincroniza valueCheck cuando el prop value cambia desde el padre
  // (ej: al cargar una regla existente para edición)
  // Compara por contenido (key de values) para no entrar en loop con onChange
  const prevValueKeyRef = useRef('');
  const valueKey = value.map(v => v.value).sort().join(',');
  const internalKey = valueCheck.map(v => v.value).sort().join(',');
  if (prevValueKeyRef.current !== valueKey && valueKey !== internalKey) {
    prevValueKeyRef.current = valueKey;
    setValueCheck(value);
  }

  const onChangeCheck = (itemValue: string, itemLabel: string) => {
    const newValue = valueCheck.length > 0
      ? valueCheck.some(i => i.value === itemValue)
        ? valueCheck.filter(i => i.value !== itemValue)
        : [...valueCheck, { value: itemValue, label: itemLabel }]
      : [...valueCheck, { value: itemValue, label: itemLabel }];

    setValueCheck(newValue);
    onChange(newValue);
  };

  const selectAll = (hasCheckAll: boolean) => {
    const filtered = optionsFiltered();
    setValueCheck(hasCheckAll ? filtered : []);
    onChange(hasCheckAll ? filtered : []);
  };

  const optionsFiltered = useCallback(() => {
    if (search === '') return options;
    const searchLower = search.toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(searchLower));
  }, [search, options]);

  const validCheck = useMemo(() => {
    return new Set(valueCheck.map(i => i.value));
  }, [valueCheck]);

  const buttonSelectedTitle = useMemo(() => {
    const firstOptions = valueCheck.slice(0, 3).filter(i => i.value).map(v => v.label).join(', ');
    const differenceSelected = valueCheck.length - 3;
    return `${firstOptions}${differenceSelected > 0 ? `, +${differenceSelected}` : ''}`;
  }, [valueCheck]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  };

  const filtered = optionsFiltered();
  const totalHeight = filtered.length * ITEM_HEIGHT;

  const startIndex = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(filtered.length, Math.ceil((scrollTop + CONTAINER_HEIGHT) / ITEM_HEIGHT) + OVERSCAN);
  const visibleItems = filtered.slice(startIndex, endIndex);
  const offsetTop = startIndex * ITEM_HEIGHT;

  return (
    <div className="flex flex-col relative min-full">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={showOptions}
        aria-label={valueCheck.length > 0 ? `Multiselect: ${buttonSelectedTitle}` : 'Seleccionar opciones'}
        onBlur={() => !mouseEnter && setShowOptions(false)}
        className="flex items-center gap-2 bg-white border border-ebony-950/15 rounded-lg h-11 px-3.5 text-gray-700 cursor-pointer"
        onClick={() => setShowOptions(!showOptions)}
      >
        <span className="truncate text-left flex-1">
          {valueCheck.length > 0 ? buttonSelectedTitle : 'Seleccionar'}
        </span>
        {showOptions ? <RiArrowUpSLine size={20} color="#475569" aria-hidden="true" /> : <RiArrowDownSLine size={20} color="#475569" aria-hidden="true" />}
      </button>

      {showOptions && (
        <div
          role="listbox"
          aria-multiselectable="true"
          onMouseOver={() => setMouseEnter(true)}
          onMouseOut={() => setMouseEnter(false)}
          onBlur={() => !mouseEnter && setShowOptions(false)}
          className={'bg-white rounded-lg mt-1 shadow-sm absolute top-11 z-10 w-full border border-gray-200'}
        >
          {isSearchable && (
            <div className="flex w-full items-center px-3 py-2 gap-3">
              <RiSearchLine size={20} color="#94A3B8" />
              <input
                type="text"
                placeholder={'Buscar...'}
                className="w-full outline-0"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}

          <hr />

          <ul className="w-full flex items-center p-3 text-sm text-gray-700 border-t-0">
            <li className="flex items-center w-full">
            <input
              id="selectAll"
              checked={valueCheck.length === filtered.length && filtered.length > 0}
              onChange={() => selectAll(!(valueCheck.length === filtered.length))}
              type="checkbox"
              value=""
              className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-teal-500 dark:focus:ring-teal-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-green-700"
            />
            <div className="flex flex-col">
              <label htmlFor="selectAll" className="ms-2 text-sm font-medium text-black cursor-pointer">
                Seleccionar todas
              </label>
            </div>
            </li>
          </ul>
          <hr />

          {filtered.length === 0 ? (
            <h2 className="p-3">Sin resultados</h2>
          ) : (
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="overflow-y-auto"
              style={{ height: Math.min(CONTAINER_HEIGHT, totalHeight) }}
            >
              <div style={{ height: totalHeight, position: 'relative' }}>
                <ul style={{ position: 'absolute', top: offsetTop, left: 0, right: 0 }}>
                  {visibleItems.map((item, i) => {
                    const globalIndex = startIndex + i;
                    return (
                      <li
                        key={`${item.value}-${globalIndex}`}
                        role="option"
                        aria-selected={validCheck.has(item.value)}
                        className="w-full flex items-center px-3 text-sm text-gray-700"
                        style={{ height: ITEM_HEIGHT }}
                      >
                        <input
                          id={`opt-${globalIndex}`}
                          checked={validCheck.has(item.value)}
                          onChange={() => onChangeCheck(item.value, item.label)}
                          type="checkbox"
                          value=""
                          className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-teal-500 dark:focus:ring-teal-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 accent-green-700"
                        />
                        <label
                          htmlFor={`opt-${globalIndex}`}
                          className="ms-2 text-sm font-medium text-black cursor-pointer truncate"
                        >
                          {item.label}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
